import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import MainLayout from '@/components/layout/MainLayout';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { ChevronLeft, ArrowRight } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/components/ui/use-toast';
import { motion } from 'framer-motion';
import { fadeInVariants } from '@/lib/animations';

// Mock data for journey details
const journeysData = {
  '1': {
    id: '1',
    title: 'Redução de Ansiedade',
    description: 'Um programa de 7 dias para desenvolver técnicas de gerenciamento da ansiedade',
    days: 7,
    category: 'Ansiedade',
    image: 'https://images.unsplash.com/photo-1506126613408-eca07ce68773?q=80&w=300&auto=format',
    steps: [
      {
        id: 1,
        title: 'Consciência da Respiração',
        description: 'Aprenda técnicas básicas de respiração para acalmar o sistema nervoso.',
        content: 'Neste exercício, você vai praticar a respiração diafragmática por 5 minutos. Inspire pelo nariz contando até 4, segure por 2, e expire lentamente pela boca contando até 6. Repita este ciclo durante todo o exercício.',
        duration: '5 minutos'
      },
      {
        id: 2,
        title: 'Escaneamento Corporal',
        description: 'Identifique tensões no corpo causadas pela ansiedade.',
        content: 'Deite-se confortavelmente e dirija sua atenção para diferentes partes do corpo, dos pés à cabeça. Observe sensações sem julgamento e tente relaxar áreas tensas.',
        duration: '10 minutos'
      },
      {
        id: 3,
        title: 'Identificando Gatilhos',
        description: 'Reconheça o que desencadeia sua ansiedade.',
        content: 'Reflita sobre situações recentes que causaram ansiedade e anote padrões que você percebe. Tente identificar pensamentos específicos que surgem nesses momentos.',
        duration: '15 minutos'
      },
      {
        id: 4,
        title: 'Reestruturação Cognitiva',
        description: 'Questione pensamentos ansiosos.',
        content: 'Escolha um pensamento ansioso recorrente e questione sua validade. Existem evidências que o apoiam? Existem perspectivas alternativas? Registre suas reflexões.',
        duration: '20 minutos'
      },
      {
        id: 5,
        title: 'Mindfulness em Situações Difíceis',
        description: 'Pratique estar presente durante momentos de estresse.',
        content: 'Escolha uma atividade diária que normalmente cause ansiedade. Pratique mantendo a atenção no momento presente, observando pensamentos e sensações sem reagir a eles.',
        duration: '15 minutos'
      },
      {
        id: 6,
        title: 'Técnicas de Grounding',
        description: 'Aprenda a se reconectar com o presente durante crises de ansiedade.',
        content: 'Pratique a técnica 5-4-3-2-1: identifique 5 coisas que você vê, 4 coisas que pode tocar, 3 coisas que pode ouvir, 2 coisas que pode cheirar e 1 coisa que pode provar.',
        duration: '10 minutos'
      },
      {
        id: 7,
        title: 'Plano de Manutenção',
        description: 'Crie estratégias para gerenciar a ansiedade a longo prazo.',
        content: 'Revise as técnicas que funcionaram melhor para você e desenvolva um plano personalizado de 5-10 minutos diários para manter o controle sobre a ansiedade.',
        duration: '15 minutos'
      }
    ]
  },
  '2': {
    id: '2',
    title: 'Mindfulness para Iniciantes',
    description: 'Aprenda os fundamentos da atenção plena neste programa de 5 dias',
    days: 5,
    category: 'Mindfulness',
    image: 'https://images.unsplash.com/photo-1593811167562-9cef47bfc4d7?q=80&w=300&auto=format',
    steps: [
      {
        id: 1,
        title: 'Introdução ao Mindfulness',
        description: 'Compreenda os princípios básicos da atenção plena',
        content: 'O mindfulness é a prática de prestar atenção ao momento presente de forma intencional e sem julgamento. Nesta introdução, aprenderemos a reconhecer quando nossa mente divaga e a trazê-la de volta ao presente.',
        duration: '10 minutos'
      },
      {
        id: 2,
        title: 'Respiração Consciente',
        description: 'Use a respiração como âncora para o momento presente',
        content: 'Encontre uma posição confortável e foque sua atenção na respiração. Observe o ar entrando e saindo, as sensações no corpo, sem tentar controlar a respiração. Quando perceber que sua mente divagou, gentilmente traga-a de volta.',
        duration: '15 minutos'
      },
      {
        id: 3, 
        title: 'Comer com Consciência',
        description: 'Aplique o mindfulness a uma atividade cotidiana',
        content: 'Escolha uma pequena refeição ou lanche. Antes de comer, observe a aparência, o cheiro e a textura. Ao comer, saboreie cada mordida, notando os sabores e sensações. Perceba como seu corpo responde ao alimento.',
        duration: '20 minutos'
      },
      {
        id: 4,
        title: 'Escaneamento Corporal',
        description: 'Desenvolva consciência corporal através da atenção plena',
        content: 'Deite-se em um local confortável e direcione sua atenção para diferentes partes do corpo, dos pés à cabeça. Observe sensações sem julgamento, apenas com curiosidade e aceitação.',
        duration: '15 minutos'
      },
      {
        id: 5,
        title: 'Integrando o Mindfulness ao Dia a Dia',
        description: 'Estabeleça uma prática regular de atenção plena',
        content: 'Identifique momentos específicos do seu dia que podem servir como lembretes para praticar mindfulness. Crie um plano para incorporar micro-práticas (1-2 minutos) ao longo do dia, além de uma prática formal mais longa.',
        duration: '10 minutos'
      }
    ]
  },
  '3': {
    id: '3',
    title: 'Melhor Sono',
    description: 'Desenvolva hábitos para um sono reparador neste programa de 10 dias',
    days: 10,
    category: 'Sono',
    image: 'https://images.unsplash.com/photo-1455642305367-68834a9d8540?q=80&w=300&auto=format',
    steps: [
      {
        id: 1,
        title: 'Avaliando seu Sono',
        description: 'Entenda seus padrões atuais de sono',
        content: 'Registre seus hábitos de sono por 3 dias, anotando horários de deitar e acordar, qualidade percebida do sono, e fatores que possam ter interferido no seu descanso.',
        duration: '15 minutos'
      },
      {
        id: 2,
        title: 'Criando um Ambiente Propício',
        description: 'Otimize seu quarto para um sono de qualidade',
        content: 'Avalie seu ambiente de sono considerando temperatura, luz, ruído e conforto. Faça ajustes necessários para criar um espaço mais propício ao descanso.',
        duration: '30 minutos'
      },
      // Outros passos seriam adicionados aqui para completar os 10 dias
    ]
  }
};

const JourneyDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [journey, setJourney] = useState<any>(null);
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [completedSteps, setCompletedSteps] = useState<number[]>([]);

  useEffect(() => {
    if (!id) return;
    
    const journeyData = journeysData[id as keyof typeof journeysData];
    
    if (!journeyData) {
      toast({
        title: "Jornada não encontrada",
        description: "A jornada solicitada não está disponível",
        variant: "destructive"
      });
      navigate('/jornadas');
      return;
    }
    
    setJourney(journeyData);
    
    // Carregar progresso do localStorage
    const savedProgress = localStorage.getItem(`journey_progress_${id}`);
    if (savedProgress) {
      const progress = JSON.parse(savedProgress);
      setCurrentStep(progress.currentStep || 1);
      setCompletedSteps(progress.completedSteps || []);
    }
  }, [id, navigate, toast]);

  const handleStepComplete = () => {
    if (!journey) return;
    
    const newCompletedSteps = [...completedSteps];
    if (!newCompletedSteps.includes(currentStep)) {
      newCompletedSteps.push(currentStep);
    }
    
    setCompletedSteps(newCompletedSteps);
    
    // Salvar progresso
    localStorage.setItem(`journey_progress_${id}`, JSON.stringify({
      currentStep: currentStep,
      completedSteps: newCompletedSteps
    }));
    
    toast({
      title: "Etapa concluída!",
      description: `Você concluiu a etapa ${currentStep} de ${journey.steps.length}`,
    });
    
    // Se não for a última etapa, avançar para a próxima
    if (currentStep < journey.steps.length) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleSelectStep = (stepNumber: number) => {
    setCurrentStep(stepNumber);
  };

  if (!journey) {
    return (
      <MainLayout>
        <div className="container mx-auto p-6 flex justify-center items-center min-h-[50vh]">
          <p>Carregando jornada...</p>
        </div>
      </MainLayout>
    );
  }

  const progress = completedSteps.length > 0 
    ? Math.round((completedSteps.length / journey.steps.length) * 100) 
    : 0;
  
  const currentStepData = journey.steps[currentStep - 1];

  return (
    <MainLayout>
      <div className="container mx-auto p-6">
        <Button 
          variant="ghost" 
          className="mb-6 flex items-center gap-2" 
          onClick={() => navigate('/jornadas')}
        >
          <ChevronLeft size={16} /> Voltar para jornadas
        </Button>
        
        <motion.div
          variants={fadeInVariants}
          initial="initial"
          animate="animate"
          className="mb-8"
        >
          <div className="flex justify-between items-center mb-4">
            <div>
              <h1 className="text-2xl font-bold">{journey.title}</h1>
              <p className="text-muted-foreground">{journey.description}</p>
            </div>
            <Badge variant="outline" className="text-sm">{journey.category}</Badge>
          </div>
          
          <div className="mb-6">
            <div className="flex justify-between text-sm mb-2">
              <span>Progresso da Jornada</span>
              <span>{completedSteps.length} de {journey.steps.length} etapas</span>
            </div>
            <Progress value={progress} className="h-2" />
          </div>
        </motion.div>
        
        <div className="grid md:grid-cols-3 gap-6">
          <div className="md:col-span-1">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Etapas da Jornada</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <ul className="divide-y">
                  {journey.steps.map((step: any) => (
                    <li 
                      key={step.id}
                      className={`px-4 py-3 cursor-pointer hover:bg-muted transition-colors ${
                        currentStep === step.id ? 'bg-muted' : ''
                      }`}
                      onClick={() => handleSelectStep(step.id)}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium">{step.id}. {step.title}</span>
                        {completedSteps.includes(step.id) && (
                          <Badge variant="secondary" className="bg-green-500 text-white">Concluído</Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">{step.duration}</p>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>
          
          <div className="md:col-span-2">
            <motion.div
              key={currentStepData.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              <Card className="h-full flex flex-col">
                <CardHeader>
                  <div className="flex justify-between items-center">
                    <CardTitle>{currentStepData.title}</CardTitle>
                    <Badge variant="outline">{currentStepData.duration}</Badge>
                  </div>
                  <p className="text-muted-foreground">{currentStepData.description}</p>
                </CardHeader>
                <CardContent className="flex-1">
                  <p className="whitespace-pre-line">{currentStepData.content}</p>
                </CardContent>
                <CardFooter className="mt-auto flex justify-between">
                  <Button 
                    variant="outline" 
                    onClick={() => currentStep > 1 && setCurrentStep(currentStep - 1)}
                    disabled={currentStep <= 1}
                  >
                    Etapa anterior
                  </Button>
                  <Button 
                    onClick={handleStepComplete}
                    className="gap-2"
                    disabled={completedSteps.includes(currentStep)}
                  >
                    {completedSteps.includes(currentStep) 
                      ? 'Etapa concluída' 
                      : 'Marcar como concluída'}
                    <ArrowRight size={16} />
                  </Button>
                </CardFooter>
              </Card>
            </motion.div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default JourneyDetail;
